// Module: flow-client
// Role: React Query hooks for Flow Engine API endpoints
// Uses: ./client:apiFetch, ./flow-types, @tanstack/react-query,
//       ../lib/abortTimeout:timeoutSignal, ../lib/uploadTelemetry:PRESIGN_TIMEOUT_MS|SUBMIT_TIMEOUT_MS
// Used by: miniapp UI components (flow renderer)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { apiFetch } from './client';
import { timeoutSignal } from '../lib/abortTimeout';
import { PRESIGN_TIMEOUT_MS, SUBMIT_TIMEOUT_MS } from '../lib/uploadTelemetry';
import type {
  GetFlowResponse,
  PresignRequest,
  PresignResponse,
  SubmitCheckpointRequest,
  SubmitCheckpointResponse,
  CheckpointAttempt,
  RecordConsentsRequest,
  RecordConsentsResponse,
} from './flow-types';

/** BUG-1223: named polling intervals — change here to change everywhere */
export const POLL_PENDING_MS = 2000;
export const POLL_ESCALATED_MS = 12_000; // slower than pending: human moderation is slow (hours), reduce server load

export function hasPendingAttempt(data: GetFlowResponse): boolean {
  const cpId = data.next_checkpoint?.id;
  if (!cpId) return false;
  return data.attempts.some(
    (a: CheckpointAttempt) =>
      a.checkpoint_id === cpId && a.verdict === 'pending',
  );
}

/** BUG-1223: mirrors hasPendingAttempt but for escalated_to_moderation verdict.
 * Used to continue polling after human moderation escalation so the screen
 * self-updates when the moderator makes a decision (buyer may still be on screen). */
export function hasEscalatedAttempt(data: GetFlowResponse): boolean {
  const cpId = data.next_checkpoint?.id;
  if (!cpId) return false;
  return data.attempts.some(
    (a: CheckpointAttempt) =>
      a.checkpoint_id === cpId && a.verdict === 'escalated_to_moderation',
  );
}

export function useFlow(slotId: string) {
  return useQuery<GetFlowResponse>({
    queryKey: ['flow', slotId],
    queryFn: () =>
      apiFetch<GetFlowResponse>(`/api/v2/buyer/slots/${slotId}/flow`),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (hasPendingAttempt(data)) return POLL_PENDING_MS;
      if (hasEscalatedAttempt(data)) return POLL_ESCALATED_MS;
      return false;
    },
  });
}

export function useArtefactPresign(slotId: string) {
  return useMutation<PresignResponse, Error, PresignRequest>({
    mutationFn: (body) =>
      apiFetch<PresignResponse>(
        `/api/v2/buyer/slots/${slotId}/artefact/presign`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          signal: timeoutSignal(PRESIGN_TIMEOUT_MS),
        },
      ),
  });
}

export function useSubmitCheckpoint(slotId: string, cpId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    SubmitCheckpointResponse,
    Error,
    SubmitCheckpointRequest
  >({
    mutationFn: (body) =>
      apiFetch<SubmitCheckpointResponse>(
        `/api/v2/buyer/slots/${slotId}/checkpoints/${cpId}/submit`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          signal: timeoutSignal(SUBMIT_TIMEOUT_MS),
        },
      ),
    onSuccess: (response) => {
      const queryKey = ['flow', slotId];
      void queryClient.invalidateQueries({ queryKey });
      const delay = Math.max(750, Math.min(response.poll_after_ms ?? 2000, 3000));
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey });
        void queryClient.refetchQueries({ queryKey, type: 'active' });
      }, delay);
    },
  });
}

export function useDevAcceptCheckpoint(slotId: string, cpId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; attempt_id: string; verdict: 'accepted' },
    Error,
    void
  >({
    mutationFn: () =>
      apiFetch<{ success: boolean; attempt_id: string; verdict: 'accepted' }>(
        `/api/v2/buyer/slots/${slotId}/checkpoints/${cpId}/dev-accept`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      const queryKey = ['flow', slotId];
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.refetchQueries({ queryKey, type: 'active' });
    },
  });
}

const POLL_TIMEOUT_MS = 60_000;

export function usePollAttempt(attemptId: string, enabled: boolean) {
  const startRef = useRef<number | null>(null);

  return useQuery({
    queryKey: ['attempt', attemptId],
    queryFn: async () => {
      if (startRef.current === null) {
        startRef.current = Date.now();
      }
      return apiFetch<SubmitCheckpointResponse>(
        `/api/v2/buyer/checkpoints/attempts/${attemptId}`,
      );
    },
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (data.verdict !== null && data.verdict !== 'pending') return false;
      if (
        startRef.current !== null &&
        Date.now() - startRef.current >= POLL_TIMEOUT_MS
      ) {
        return false;
      }
      return 2000;
    },
  });
}

/** FTR-1315: Record 152-FZ consent scopes via POST /api/v2/buyer/flow/consents.
 *  Scopes + version come from checkpoint.metadata.required_consents (data-driven).
 *  Backend computes ip_hash/ua_hash server-side. Idempotent (repeated calls = inserted_count=0). */
export function useRecordConsents() {
  return useMutation<RecordConsentsResponse, Error, RecordConsentsRequest>({
    mutationFn: (body) =>
      apiFetch<RecordConsentsResponse>('/api/v2/buyer/flow/consents', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
